import {t} from "@lingui/core/macro";
import type pptxgen from "pptxgenjs";

import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import type {GuideComponents} from "@khan/guide-core";
import {RequestInfo} from "@khan/shared-core";
import {staticUrl} from "@khan/urls";

import {
    black,
    blue,
    darkBeige,
    h3Size,
    lineSpacing,
    padding,
    paragraphAfter,
    purple,
    qTextY,
    slideH,
    slideW,
    textBodyMaxW,
    toInches,
} from "./constants.ts";
import {markdownToTextProps} from "./text-formatting.ts";

export function createTitleSlide(
    slide: pptxgen.Slide,
    slides: GuideComponents["CreateSlides"]["output"]["slides"],
) {
    const title = slides[0].title;

    slide.background = {color: purple};

    slide.addImage({
        path: staticUrl("/images/coach/slideshow-assets/cursor.png"),
        w: 0.66,
        h: 0.76,
        x: 7,
        y: 3.5,
    });

    slide.addImage({
        path: staticUrl("/images/coach/slideshow-assets/squiggle-blue.png"),
        w: 5,
        h: 3.15,
        x: 0,
        y: 1,
    });

    slide.addText(title, {
        x: padding,
        y: 2.1,
        w: textBodyMaxW - 0.5,
        h: 1,
        fontSize: 40,
        fontFace: "Lato",
        bold: true,
        color: black,
        valign: "top",
        lineSpacingMultiple: 0.9,
    });

    const datePretty = new Date().toLocaleDateString(RequestInfo.kaLocale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    slide.addText(datePretty, {
        x: padding,
        y: 1.55,
        w: textBodyMaxW,
        h: 0.5,
        fontSize: 18,
        fontFace: "Lato",
        color: color.offBlack,
        transparency: 36,
        valign: "top",
    });
}

export function createExitTicketSlide(
    slide: pptxgen.Slide,
    slideData: GuideComponents["CreateSlides"]["output"]["slides"][0],
) {
    slide.background = {color: darkBeige};
    slide.addText(t`CHECK FOR UNDERSTANDING`, {
        x: padding,
        y: qTextY,
        w: slideW - 2 * padding,
        h: 1,
        fontSize: 12,
        fontFace: "Lato",
        color: black,
        transparency: 36,
        valign: "top",
    });
    slide.addText(markdownToTextProps(slideData.content || ""), {
        x: padding,
        y: qTextY + toInches(spacing.xLarge_32),
        w: textBodyMaxW,
        h: 4,
        fontSize: 24,
        fontFace: "Lato",
        color: black,
        valign: "top",
        paraSpaceAfter: paragraphAfter,
        lineSpacingMultiple: lineSpacing,
    });
}

export function addQuestionContent(
    slide: pptxgen.Slide,
    slideData: GuideComponents["CreateSlides"]["output"]["slides"][0],
) {
    slide.background = {color: blue};
    slide.addText(slideData.title.toUpperCase(), {
        x: padding,
        y: qTextY,
        w: slideW - 2 * padding,
        h: 1,
        fontSize: h3Size,
        fontFace: "Lato",
        color: black,
        transparency: 36,
        valign: "top",
    });
    slide.addText(markdownToTextProps(slideData.content || ""), {
        x: padding,
        y: qTextY + toInches(spacing.xLarge_32),
        w: textBodyMaxW,
        h: 5,
        fontSize: 24,
        fontFace: "Lato",
        color: black,
        valign: "top",
        paraSpaceAfter: paragraphAfter,
        lineSpacingMultiple: lineSpacing,
    });
}

export function addYoutubeContent(
    slide: pptxgen.Slide,
    slideData: GuideComponents["CreateSlides"]["output"]["slides"][0],
    pres: pptxgen,
) {
    const vidW = 5;
    const vidH = (vidW * slideH) / slideW;
    const vidY = (slideH - vidH) / 2 - 0.5;
    const vidX = (slideW - vidW) / 2;
    slide.background = {color: purple};
    slide.addShape(pres.ShapeType.roundRect, {
        fill: {color: "B392E4"},
        w: vidW,
        h: vidH,
        x: vidX,
        y: vidY,
        rectRadius: 0.15,
        hyperlink: {url: slideData.content},
    });
    const playSize = 0.7;
    slide.addImage({
        path: staticUrl("/images/coach/slideshow-assets/play-icon.png"),
        x: (slideW - playSize) / 2,
        y: vidY + (vidH - playSize) / 2,
        w: playSize,
        h: playSize,
        hyperlink: {url: slideData.content},
    });
    slide.addImage({
        path: staticUrl("/images/coach/slideshow-assets/cursor.png"),
        w: 0.66,
        h: 0.76,
        x: vidX + vidW - 0.33,
        y: vidY + vidH - toInches(spacing.xLarge_32),
        rotate: 5,
    });
    slide.addImage({
        path: staticUrl(
            "/images/coach/slideshow-assets/khanmigo-look-bottom-left.png",
        ),
        x: vidX + vidW - 1,
        y: vidY - 0.35,
        w: playSize * 1.1,
        h: playSize * 1.15,
        rotate: 5,
    });
    slide.addText(t`WATCH`, {
        x: vidX - toInches(spacing.xSmall_8),
        y: vidY + vidH + toInches(spacing.medium_16),
        w: vidW * 0.5,
        h: 0.25,
        fontSize: h3Size,
        fontFace: "Lato",
        valign: "top",
        color: black,
        transparency: 32,
    });
    slide.addText(slideData.title, {
        x: vidX - toInches(spacing.xSmall_8),
        y: vidY + vidH + toInches(spacing.xLarge_32 + spacing.xSmall_8),
        w: vidW * 0.75,
        h: 1,
        fontSize: 22,
        fontFace: "Lato",
        valign: "top",
        color: black,
        lineSpacingMultiple: 0.9,
    });
}

export function addEndSlideContent(slide: pptxgen.Slide, pres: pptxgen) {
    const bbly = 2;
    const bblX = 4;
    const nubSize = 0.3;
    slide.background = {color: "F3E6D1"};
    slide.addImage({
        path: staticUrl("/images/coach/slideshow-assets/khanmigo-blushing.gif"),
        w: 1.75,
        h: 1.75,
        x: bblX - 1.75 - toInches(spacing.medium_16),
        y: bbly - toInches(spacing.xLarge_32),
    });

    const congrats = t`You did it! 🎉`;
    slide.addText(congrats, {
        shape: pres.ShapeType.roundRect,
        fill: {color: "FFFFFF"},
        x: bblX,
        y: bbly,
        fontSize: 28,
        fontFace: "Lato",
        color: black,
        align: "center",
        valign: "middle",
        w: toInches(
            congrats.length * (spacing.medium_16 + spacing.xxxxSmall_2),
        ),
        h: 1,
    });
    slide.addShape(pres.ShapeType.rect, {
        fill: {color: "FFFFFF"},
        w: nubSize,
        h: nubSize,
        x: bblX,
        y: bbly + 1 - nubSize,
    });
}

export function addGeneralSlideContent(
    slide: pptxgen.Slide,
    slideData: GuideComponents["CreateSlides"]["output"]["slides"][0],
) {
    slide.background = {color: purple};
    slide.addText(slideData.title, {
        x: padding,
        y: 0.65,
        w: slideW - 2 * padding,
        h: 1,
        fontSize: 30,
        fontFace: "Lato",
        bold: true,
        color: black,
        valign: "top",
    });
    slide.addText(markdownToTextProps(slideData.content || ""), {
        x: padding,
        y: 1.5,
        w: textBodyMaxW,
        h: 4,
        fontSize: 18,
        fontFace: "Lato",
        color: black,
        transparency: 36,
        valign: "top",
        paraSpaceAfter: paragraphAfter,
        lineSpacingMultiple: lineSpacing,
    });
}
